import { emptyApi } from "./emptyApi";
import qs from "qs";
import { BillingInvoice, RefundDetails } from "types/Models/billing";
import { IncompletePaymentIntent } from "./subscriptions";
import { FileObject } from "types/Models/fileObject";

type Args = {
  groupId: string;
  pageNumber: number;
  searchTerm?: string | null;
  selectedSeasons?: string;
  productTypes?: string;
  entityId: string;
  paymentStatuses?: string;
  exportAction?: "EXPORT_INVOICES" | "EXPORT_INVOICE_DOCUMENTS";
  sortBy?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  app_types?: string | null;
  billing_types?: string | null;
};

type ListReponse = {
  group_invoices: BillingInvoice[];
  total_pages: number;
  current_page: number;
  total_count: number;
  per_page: number;
  export_file: FileObject;
  due_amount: number;
  draft_invoices_count: number;
  all_invoices_count: number;
};

export const billingInvoiceApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getBillingInvoices: build.query<ListReponse, Args>({
      query: ({
        groupId,
        pageNumber,
        searchTerm,
        selectedSeasons,
        productTypes,
        paymentStatuses,
        entityId,
        exportAction,
        sortBy,
        fromDate,
        toDate,
        app_types,
        billing_types,
      }) => {
        let queryUrl = qs.stringify(
          {
            page_num: pageNumber,
            search_term: searchTerm,
            seasons: selectedSeasons,
            product_types: productTypes,
            payment_statuses: paymentStatuses,
            export_action: exportAction,
            sort_by: sortBy,
            from_date: fromDate,
            to_date: toDate,
            app_types,
            billing_types,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/invoices/${queryUrl}`,
        };
      },
      providesTags: () => ["BillingInvoices"],
    }),

    createBillingInvoice: build.mutation<
      BillingInvoice,
      {
        groupId: string;
        task_id: string;
        invoice_amount: string;
        task_short_name: string;
        invoice_type:
          | "ONE_TIME"
          | "RECURRING"
          | "BALANCING_PAYMENT"
          | "MONTHLY_BOOKKEEPING";
        description?: string;
        season: string;
        entityId: string;
        month?: string;
        expense_amount?: string;
        is_auto_pay_enabled?: boolean;
      }
    >({
      query: ({
        groupId,
        task_id,
        invoice_amount,
        task_short_name,
        invoice_type,
        description,
        season,
        entityId,
        month,
        expense_amount,
        is_auto_pay_enabled,
      }) => {
        return {
          url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/invoice/`,
          method: "POST",
          body: {
            task_id,
            invoice_amount,
            task_short_name,
            invoice_type,
            description,
            season,
            month: Number(month),
            expense_amount,
            is_auto_pay_enabled,
          },
        };
      },
      invalidatesTags: ["BillingInvoices"],
    }),

    searchCompanyGroupInvoices: build.query<
      Omit<ListReponse, "group_invoices"> & { invoices: BillingInvoice[] },
      {
        search_term: string | null;
        company_groups_ids: string[];
        page_num: number;
        service?: string;
        spoc_ids?: string;
      }
    >({
      query: ({
        search_term,
        company_groups_ids,
        page_num,
        service,
        spoc_ids,
      }) => {
        return {
          url: `/api/inkle/billing/all_company_group_invoices/`,
          params: {
            search_term,
            company_groups_ids: company_groups_ids.join(","),
            page_num,
            service,
            spoc_ids,
          },
        };
      },
      providesTags: ["BillingInvoices"],
    }),

    updateBillingInvoice: build.mutation<
      BillingInvoice,
      {
        groupId: string;
        task_id: string;
        invoice_amount: string;
        task_short_name: string;
        invoice_type:
          | "ONE_TIME"
          | "RECURRING"
          | "BALANCING_PAYMENT"
          | "MONTHLY_BOOKKEEPING";
        description?: string;
        season: string;
        entityId: string;
        month?: string;
        expense_amount?: string;
        is_auto_pay_enabled?: boolean;
        invoice_id: string;
        status?: "PENDING";
      }
    >({
      query: ({
        groupId,
        task_id,
        invoice_amount,
        task_short_name,
        invoice_type,
        description,
        season,
        entityId,
        month,
        expense_amount,
        is_auto_pay_enabled,
        invoice_id,
        status,
      }) => {
        const payload = {
          task_id,
          invoice_amount,
          task_short_name,
          invoice_type,
          description,
          season,
          month: Number(month),
          expense_amount,
          is_auto_pay_enabled,
          status,
        };

        return {
          url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/invoice/${invoice_id}/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: ["BillingInvoices"],
    }),

    getBillingInvoice: build.query<
      BillingInvoice,
      { groupId: string; entityId: string; invoiceId: string }
    >({
      query: ({ groupId, entityId, invoiceId }) => {
        return {
          url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/`,
        };
      },
      providesTags: (result) =>
        result ? [{ type: "SingleBillingInvoice" }] : [],
    }),

    payMultipleInvoice: build.mutation<
      IncompletePaymentIntent,
      {
        groupId: string;
        entityId: string;
        payload: {
          invoices: string[];
          credits_used?: number | null;
          pending_credit_ledger_record?: string | null;
          balancing_payment_message_id?: string;
          brex_usd_used?: number;
          brex_quote_id?: string;
          service_team_id?: string;
        };
      }
    >({
      query: ({ payload, groupId, entityId }) => ({
        url: `/api/inkle/payments/${groupId}/entity/${entityId}/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "BillingInvoices" }] : [],
    }),

    deleteBillingInvoice: build.mutation<
      BillingInvoice,
      {
        groupId: string;
        invoiceId: string;
        entityId: string;
      }
    >({
      query: ({ groupId, invoiceId, entityId }) => {
        return {
          url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/invoice/`,
          method: "DELETE",
          body: { invoice_id: invoiceId },
        };
      },
      invalidatesTags: () => [{ type: "BillingInvoices" }],
    }),

    createCreditInvoice: build.mutation<
      BillingInvoice,
      {
        groupId: string;
        entityId: string;
        invoice_amount: number;
      }
    >({
      query: ({ groupId, invoice_amount, entityId }) => {
        return {
          url: `/api/inkle/billing/group/${groupId}/entity/${entityId}/purchased_credits_invoice/`,
          method: "POST",
          body: {
            invoice_amount,
          },
        };
      },
      invalidatesTags: (result) =>
        result ? [{ type: "BillingInvoices" }] : [],
    }),
    initiateRefund: build.mutation<
      RefundDetails,
      {
        refund_amount: number;
        invoice_id: string;
        refund_reason: string;
        refund_destination: string;
        refund_type: string;
        groupId: string;
      }
    >({
      query: ({
        refund_amount,
        invoice_id,
        refund_reason,
        refund_destination,
        refund_type,
        groupId,
      }) => {
        return {
          url: `/api/inkle/billing/group/${groupId}/refunds/`,
          method: "POST",
          body: {
            refund_amount,
            invoice_id,
            refund_reason,
            refund_destination,
            refund_type,
          },
        };
      },
      invalidatesTags: ["BillingInvoices", "CREDITS"],
    }),
    revokeLateFee: build.mutation<
      {},
      {
        invoice_id: string;
      }
    >({
      query: ({ invoice_id }) => {
        return {
          url: `/api/inkle/billing/service-team/invoice/${invoice_id}/`,
          method: "PUT",
        };
      },
      invalidatesTags: ["BillingInvoices"],
    }),

    bulkAcceptInvoices: build.mutation<
      {},
      {
        entityId: string;
        invoice_ids: string[];
      }
    >({
      query: ({ entityId, invoice_ids }) => {
        return {
          url: `/api/inkle/billing/entity/${entityId}/invoice/bulk_invoices_action/`,
          method: "PUT",
          body: { invoice_ids },
        };
      },
      invalidatesTags: ["BillingInvoices"],
    }),

    bulkRejectInvoices: build.mutation<
      {},
      {
        entityId: string;
        invoice_ids: string[];
      }
    >({
      query: ({ entityId, invoice_ids }) => {
        return {
          url: `/api/inkle/billing/entity/${entityId}/invoice/bulk_invoices_action/`,
          method: "DELETE",
          body: { invoice_ids },
        };
      },
      invalidatesTags: ["BillingInvoices"],
    }),

    bulkAcceptInvoicesAdmin: build.mutation<
      {},
      {
        invoice_ids: string[];
      }
    >({
      query: ({ invoice_ids }) => {
        return {
          url: `/api/inkle/billing/all_company_group_invoices/`,
          method: "PUT",
          body: { invoice_ids },
        };
      },
      invalidatesTags: ["BillingInvoices"],
    }),

    bulkRejectInvoicesAdmin: build.mutation<
      {},
      {
        invoice_ids: string[];
      }
    >({
      query: ({ invoice_ids }) => {
        return {
          url: `/api/inkle/billing/all_company_group_invoices/`,
          method: "DELETE",
          body: { invoice_ids },
        };
      },
      invalidatesTags: ["BillingInvoices"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetBillingInvoicesQuery,
  useCreateBillingInvoiceMutation,
  useDeleteBillingInvoiceMutation,
  useCreateCreditInvoiceMutation,
  useLazyGetBillingInvoicesQuery,
  useGetBillingInvoiceQuery,
  usePayMultipleInvoiceMutation,
  useLazyGetBillingInvoiceQuery,
  useInitiateRefundMutation,
  useUpdateBillingInvoiceMutation,
  useRevokeLateFeeMutation,
  useBulkAcceptInvoicesMutation,
  useBulkRejectInvoicesMutation,
  useBulkAcceptInvoicesAdminMutation,
  useBulkRejectInvoicesAdminMutation,
  useSearchCompanyGroupInvoicesQuery,
} = billingInvoiceApis;
